import { Download, LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import type { PackRecord, ProfileRecord } from '../types';
import { downloadPackAsZip } from '../lib/downloads';

interface PackDownloadButtonProps {
  pack: PackRecord;
  profiles: ProfileRecord[];
  notify: (message: string, tone?: 'success' | 'error') => void;
  className?: string;
  label?: string;
}

export function PackDownloadButton({ pack, profiles, notify, className = 'button secondary', label = 'Download pack' }: PackDownloadButtonProps) {
  const [progress, setProgress] = useState<[number, number] | null>(null);

  async function download(): Promise<void> {
    try {
      setProgress([0, profiles.length]);
      await downloadPackAsZip(pack, profiles, (completed, total) => setProgress([completed, total]));
      notify(`${pack.name} downloaded as a ZIP archive.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'The profile pack could not be downloaded.', 'error');
    } finally {
      setProgress(null);
    }
  }

  return (
    <button
      className={className}
      type="button"
      onClick={() => void download()}
      disabled={Boolean(progress)}
      aria-label={`${progress ? `Preparing ${progress[0]} of ${progress[1]} profiles from` : label}: ${pack.name}`}
      aria-live="polite"
    >
      {progress ? <LoaderCircle className="spin" size={16} /> : <Download size={16} />}
      {progress ? `Preparing ${progress[0]}/${progress[1]}` : label}
    </button>
  );
}
